// Copyright (c) Docugami, Inc. All rights reserved.

import { useDocumentSearch } from "hooks/DocumentSearch"
import React, { useEffect, useRef, useState } from "react";
import { SearchResult } from "search/TextSearch";
import { useChunkSelection } from "hooks/chunkSelection";
import { TokenRange } from "networking/api";
import { SearchBar } from "./SearchBar";
import { modFloor } from "utils/MathUtils";
import { SearchNavigateToResultSchema, SearchOpenedSchema, TelemetryEvent } from "telemetry/TelemetryEvents";
import { Telemetry } from "telemetry/TelemetryManager";
import { useAppSelector } from "hooks/redux";

export interface IDocumentSearchBar {
    element: JSX.Element | null;
    currentIndex: number;
    results: TokenRange[];
}

interface DocumentSearchResult {
    query: string,
    results: SearchResult[] | null,
    tokenRangeResults: TokenRange[]
}

export const useDocumentSearchBar = (documentId: string): IDocumentSearchBar => {
    const docSearch = useDocumentSearch();
    const [currentResultIndex, setCurrentResultIndex] = useState(0);
    const [currentSearchResult, setCurrentSearchResult] = useState<DocumentSearchResult>({ query: "", results: null, tokenRangeResults: [] });
    const [opened, setOpened] = useState(false)
    const chunkSelection = useChunkSelection();
    const hasCpml = useAppSelector(state => !!state.documents.indexed[documentId]?.clusterDocument?.hasCpml);
    const inputRef = useRef<HTMLInputElement>();

    useEffect(() => {
        onClose();
        docSearch.clearSearchTree();
    },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [documentId]);

    useEffect(() => {
        const handleKeySearch = (event: KeyboardEvent) => {
            if ((event.ctrlKey && event.key === 'f') || (event.metaKey && event.key === 'f')) {
                event.preventDefault()
                onSearchButtonClick();
            }
        };

        document.addEventListener("keydown", handleKeySearch, false);
        return () => {
            document.removeEventListener("keydown", handleKeySearch, false);
        };
    });

    useEffect(() => {
        if (!opened) {
            return;
        }

        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        document.addEventListener("keydown", handleEscape, false);
        return () => {
            document.removeEventListener("keydown", handleEscape, false);
        };
    },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [opened]);

    useEffect(() => {
        if (!!currentSearchResult.results) {
            const tokenRanges: TokenRange[] = docSearch
                .getTokensForSearchResults(currentSearchResult.results, currentSearchResult.query.length)
                .map(x => { return { fromTok: x.fromTok!, toTok: x.toTok! + 1 } });

            currentSearchResult.tokenRangeResults = tokenRanges;
            setCurrentSearchResult(currentSearchResult)
            navigateToResult(0)
        }
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
        [currentSearchResult.results]);

    function onSearchButtonClick(): void {
        Telemetry.trackEvent<SearchOpenedSchema>({
            eventName: TelemetryEvent.SearchOpened
        });
        setOpened(true);
        chunkSelection.clearSelection()
        docSearch.createSearchTree();
        inputRef.current?.focus();
    }

    function onChange(ev: any): void {
        let newValue = ev.target.value;
        setCurrentResultIndex(0);
        if (!!newValue) {
            const results = docSearch.queryDocument(newValue);
            setCurrentSearchResult({
                query: newValue,
                results: results,
                tokenRangeResults: []
            })
        } else {
            onClear();
        }
    }

    function onClear(): void {
        setCurrentResultIndex(0);
        setCurrentSearchResult({
            query: "",
            results: null,
            tokenRangeResults: []
        })
    }

    function onClose(): void {
        onClear();
        setOpened(false);
    }

    function handleEnterOnSearch(ev: KeyboardEvent): void {
        if (!opened) {
            return;
        }
        if (ev.key === "Enter") {
            const index = ev.shiftKey ? getPrevIndex(currentResultIndex) : getNextIndex(currentResultIndex);
            navigateToResult(index);
        }
    }

    const getNextIndex = (currentIndex: number) => (currentIndex + 1) % currentSearchResult.results?.length!;
    const getPrevIndex = (currentIndex: number) => modFloor(currentIndex - 1, currentSearchResult.results?.length!);

    function navigateToResult(index: number): void {
        if (!currentSearchResult.results?.length) {
            return;
        }

        Telemetry.trackEvent<SearchNavigateToResultSchema>(
            {
                resultIndex: index,
                eventName: TelemetryEvent.SearchNavigateToResult
            });

        setCurrentResultIndex(index);
    }

    function getElement(): JSX.Element | null {
        if (hasCpml) {
            return (
                <SearchBar
                    inputRef={inputRef as React.RefObject<HTMLInputElement>}
                    searchIsOpen={opened}
                    currentResultIndex={currentResultIndex}
                    numberOfResults={currentSearchResult.results?.length}
                    currentValue={currentSearchResult.query}
                    onSearchIconClick={onSearchButtonClick}
                    onChange={onChange}
                    handleKeyPress={handleEnterOnSearch}
                    onNavigatePrev={() => navigateToResult(getPrevIndex(currentResultIndex))}
                    onNavigateNext={() => navigateToResult(getNextIndex(currentResultIndex))}
                    onClose={onClose} />
            )
        }

        return null;
    }

    return {
        element: getElement(),
        currentIndex: currentResultIndex,
        results: currentSearchResult.tokenRangeResults
    }
}
